import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  isValidElement,
} from "react";
import type { ComponentType, ReactNode } from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { DotsThreeVerticalIcon } from "@phosphor-icons/react/DotsThreeVertical";
import { CaretRightIcon } from "@phosphor-icons/react/CaretRight";
import { Icon } from "../Icons/Icon";
import { menuSubTriggerClasses } from "../Controls/Internal/buttonStyles";
import { headingFontStyle } from "../Theme/headingFontStyle";
import {
  MenuProvider,
  useCloseMenu,
  useShowMenuDialog,
  useIsInMenu,
} from "./Internal/MenuContext";
import { useMenuMode } from "./MenuModeContext";
import {
  MenuIconColumnContext,
  useMenuIconColumn,
} from "./MenuIconColumnContext";
import { PortalContextReset } from "../Controls/Internal/PortalContextReset";
import { usePopoverZIndex } from "../Theme/overlayZIndex";

interface MenuProps {
  trigger?: ReactNode;
  /**
   * Which edge of the menu lines up with the trigger. `end` (default) hangs it
   * to the left, which is what anything at the right edge of a row or header
   * needs. Pass `start` for a trigger in the middle of a layout — a nav bar
   * item, say — so the menu opens directly below it instead of trailing off to
   * the left.
   */
  align?: "start" | "end";
  children: ReactNode;
  width?: "default" | "wide";
  /** "dialog" renders a compact ghost trigger matching dialog close/peek buttons. */
  variant?: "default" | "dialog";
  /** Notified whenever the dropdown's open state changes (open/close). */
  onOpenChange?: ((open: boolean) => void) | undefined;
  /**
   * Set `false` when no row in this menu has a leading icon. Rows normally
   * reserve the icon column whether or not they fill it, so that a menu with
   * icons on only some rows still lines its labels up; in a menu with none it
   * is dead space that pushes every label right of its own group header.
   * Omit to inherit — a submenu follows the menu containing it.
   */
  iconColumn?: boolean | undefined;
  /**
   * Selector hook for the built-in kebab trigger. Only applies when you don't
   * pass your own `trigger` — with one, the testid belongs on that element.
   * Row-scoped tests need it: they narrow to the row, then click the kebab
   * inside it.
   */
  "data-testid"?: string | undefined;
}

const widthClasses = {
  default: "min-w-52",
  wide: "min-w-64",
};

const noop = () => {};
const inlineContextValue = {
  isInMenu: true,
  closeMenu: noop,
  showDialog: noop,
};

// A nested Menu's trigger is usually a `<Button icon>label</Button>`. As a
// submenu we don't want a button inside the sub-trigger row (interactive inside
// interactive) — pull out its icon + label and render them plainly instead.
function triggerParts(trigger: ReactNode): {
  icon?: ComponentType<{ className?: string }> | undefined;
  label: ReactNode;
} {
  if (isValidElement(trigger)) {
    const props = trigger.props as {
      icon?: ComponentType<{ className?: string }> | undefined;
      children?: ReactNode;
    };
    if (props.children !== undefined) {
      return { icon: props.icon, label: props.children };
    }
  }
  return { label: trigger };
}

/**
 * Publishes `iconColumn` to every row beneath, then renders the menu. Split in
 * two so one provider covers all three shapes below — submenu, inline and
 * dropdown — instead of being repeated at each return.
 */
export function Menu(props: MenuProps) {
  const { iconColumn, ...rest } = props;
  if (iconColumn === undefined) {
    return <MenuBody_ {...rest} />;
  }
  return (
    <MenuIconColumnContext.Provider value={iconColumn}>
      <MenuBody_ {...rest} />
    </MenuIconColumnContext.Provider>
  );
}

function MenuBody_({
  trigger,
  align = "end",
  children,
  width = "default",
  variant = "default",
  onOpenChange,
  "data-testid": dataTestid,
}: MenuProps) {
  const mode = useMenuMode();
  const isInMenu = useIsInMenu();

  // A Menu nested inside another Menu (e.g. an action that is itself a menu,
  // folded into the kebab on overflow) becomes a proper submenu — not a second
  // independent dropdown root.
  if (isInMenu) {
    const { icon, label } = triggerParts(trigger);
    return (
      <MenuSub trigger={label} icon={icon}>
        {children}
      </MenuSub>
    );
  }

  if (mode === "inline") {
    return (
      <div
        className={`${widthClasses[width]} rounded-md border border-border bg-card py-1 shadow-floating`}
      >
        <MenuProvider value={inlineContextValue}>{children}</MenuProvider>
      </div>
    );
  }

  return (
    <MenuDropdown_
      trigger={trigger}
      align={align}
      width={width}
      variant={variant}
      onOpenChange={onOpenChange}
      {...(dataTestid !== undefined && { "data-testid": dataTestid })}
    >
      {children}
    </MenuDropdown_>
  );
}

function MenuDropdown_({
  trigger,
  align = "end",
  children,
  width = "default",
  variant = "default",
  onOpenChange,
  "data-testid": dataTestid,
}: MenuProps) {
  const popoverZ = usePopoverZIndex();
  const [open, setOpenState] = useState(false);
  // Wrap the setter so every open-state transition also notifies the parent.
  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  const [activeDialog, setActiveDialog] = useState<ReactNode>(undefined);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Track whether the open was caused by hover (vs click)
  const hoverOpened = useRef(false);

  useEffect(() => {
    return () => clearTimeout(leaveTimer.current);
  }, []);

  const scheduleClose = useCallback(() => {
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => {
      hoverOpened.current = false;
      setOpen(false);
    }, 150);
  }, []);

  const cancelClose = useCallback(() => {
    clearTimeout(leaveTimer.current);
  }, []);

  const handleTriggerEnter = useCallback(() => {
    cancelClose();
    hoverOpened.current = true;
    setOpen(true);
  }, [cancelClose]);

  const handleOpenChange = useCallback((next: boolean) => {
    if (next) {
      // Click open — allow
      hoverOpened.current = false;
      setOpen(true);
    } else {
      // Radix wants to close — only allow if not hover-opened
      if (!hoverOpened.current) {
        setOpen(false);
      }
    }
  }, []);

  const forceClose = useCallback(() => {
    hoverOpened.current = false;
    setOpen(false);
  }, []);

  const showDialog = useCallback(
    (element: ReactNode) => {
      forceClose();
      setActiveDialog(element);
    },
    [forceClose],
  );

  const contextValue = useMemo(
    () => ({ isInMenu: true, closeMenu: forceClose, showDialog }),
    [forceClose, showDialog],
  );

  return (
    <>
      <DropdownMenuPrimitive.Root
        open={open}
        onOpenChange={handleOpenChange}
        modal={false}
      >
        <div
          onMouseEnter={handleTriggerEnter}
          onMouseLeave={scheduleClose}
          // Flex, not a plain block. The trigger below is inline-flex, so in a
          // block wrapper it sits on the text baseline and the wrapper grows by
          // the font's descender (~1px) — enough that the kebab rides a hair
          // higher than the buttons beside it in a control bar. Flex has no line
          // box, so the wrapper hugs the trigger exactly.
          className="flex"
        >
          <DropdownMenuPrimitive.Trigger asChild>
            {trigger ? (
              <div>{trigger}</div>
            ) : variant === "dialog" ? (
              <button
                type="button"
                {...(dataTestid !== undefined && { "data-testid": dataTestid })}
                // Brand-blue to match the dialog header's icon actions (they all
                // read as deliberate controls now, not muted ghost icons).
                className="mt-0.5 inline-flex items-center rounded-md px-2 py-1 text-blue-text transition-colors hover:bg-muted"
              >
                <DotsThreeVerticalIcon
                  className="h-3.5 w-3.5 shrink-0"
                  weight="bold"
                />
              </button>
            ) : (
              // No scale transforms on hover/active. Scaling the trigger
              // changes its bounding rect, which Floating UI tracks via
              // autoUpdate and re-positions the menu against — the menu
              // visibly shifts as the user moves the cursor toward an
              // item or presses the kebab. While the menu is open, the
              // trigger also stops responding to hover so its geometry
              // is frozen and the cursor's relative position to the
              // menu items stays constant. The dark `bg-muted` doubles
              // as visual feedback that the menu is the active surface.
              <button
                type="button"
                {...(dataTestid !== undefined && { "data-testid": dataTestid })}
                // 26px square to match the design's reduced control height so the
                // kebab lines up with sibling buttons/inputs in control
                // bars and table rows.
                className={`inline-flex h-[26px] w-[26px] items-center justify-center rounded-md border border-very-light-blue transition-colors focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:outline-none ${open ? "bg-brand-subtle" : "bg-card hover:bg-brand-subtle"}`}
              >
                <Icon
                  icon={DotsThreeVerticalIcon}
                  size="md"
                  weight="bold"
                  color="text-brand-text"
                />
              </button>
            )}
          </DropdownMenuPrimitive.Trigger>
        </div>
        <DropdownMenuPrimitive.Portal>
          <DropdownMenuPrimitive.Content
            style={{ zIndex: popoverZ }}
            // `end` by default — hangs the menu to the LEFT of its trigger.
            // That's right for anything at the right edge of a row, a header or
            // the nav bar's account block, where a left-aligned menu would run
            // off the screen.
            //
            // It is wrong for a trigger in the MIDDLE of a layout, like a nav
            // bar item: a wide menu on a narrow trigger ends up far off to its
            // left. Those pass `align="start"`.
            //
            // This can't be derived from whether the caller supplied a trigger:
            // the nav bar's items and its account block both have custom
            // triggers and want opposite answers. It depends on where the
            // trigger sits, which only the caller knows.
            align={align}
            sideOffset={4}
            collisionPadding={8}
            className={`${widthClasses[width]} max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto rounded-md border border-border bg-card px-1 py-1 shadow-floating data-[state=closed]:animate-popover-out data-[state=open]:animate-popover-in`}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            // Escape means close, even with the pointer still on the trigger —
            // handleOpenChange would otherwise refuse it as a hover-open menu.
            onEscapeKeyDown={forceClose}
            onCloseAutoFocus={(e) => {
              // Prevent Radix from refocusing the trigger on close (causes flicker)
              if (hoverOpened.current) e.preventDefault();
            }}
          >
            {/* Reset OUTSIDE the MenuProvider: sever the trigger surface's
                layout contexts (table, action bar, …) leaking through the
                portal, then re-provide the menu's own context for its items. */}
            <PortalContextReset>
              <MenuProvider value={contextValue}>{children}</MenuProvider>
            </PortalContextReset>
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuPrimitive.Root>
      {activeDialog}
    </>
  );
}

export function MenuSeparator() {
  const mode = useMenuMode();
  if (mode === "inline") {
    return <div className="my-1 border-t border-border" />;
  }
  return (
    <DropdownMenuPrimitive.Separator className="my-1 border-t border-border" />
  );
}

interface MenuGroupProps {
  label?: string;
  children: ReactNode;
}

export function MenuGroup({ label, children }: MenuGroupProps) {
  const mode = useMenuMode();
  if (mode === "inline") {
    return (
      <div>
        {label && (
          <div className="px-3 py-1.5 text-[11px] font-bold tracking-wide text-content-muted uppercase">
            {label}
          </div>
        )}
        {children}
      </div>
    );
  }
  return (
    <DropdownMenuPrimitive.Group>
      {label && (
        <DropdownMenuPrimitive.Label className="px-3 py-1.5 text-[11px] font-bold tracking-wide text-content-muted uppercase">
          {label}
        </DropdownMenuPrimitive.Label>
      )}
      {children}
    </DropdownMenuPrimitive.Group>
  );
}

interface MenuSubProps {
  trigger: ReactNode;
  /** Optional leading icon, rendered in the same icon column as menu items. */
  icon?: ComponentType<{ className?: string }> | undefined;
  children: ReactNode;
}

export function MenuSub({ trigger, icon, children }: MenuSubProps) {
  const popoverZ = usePopoverZIndex();
  const mode = useMenuMode();
  const parentClose = useCloseMenu();
  const parentShowDialog = useShowMenuDialog();
  const contextValue = useMemo(
    () => ({
      isInMenu: true,
      closeMenu: parentClose,
      showDialog: parentShowDialog,
    }),
    [parentClose, parentShowDialog],
  );

  // The icon when given; otherwise a spacer so labels line up with sibling
  // menu items, which reserve the same column. A menu that declared it has no
  // icons gets neither.
  const reserveIconColumn = useMenuIconColumn();
  const iconSlot = icon ? (
    <Icon icon={icon} size="sm" />
  ) : reserveIconColumn ? (
    <span className="w-3.5 shrink-0" />
  ) : undefined;

  if (mode === "inline") {
    return (
      <div>
        <div className="flex w-full items-center gap-2 py-1.5 pr-3 pl-3 text-[13px] font-medium text-content">
          {iconSlot}
          <span className="flex-1">{trigger}</span>
          <Icon icon={CaretRightIcon} size="xs" color="text-content-faint" />
        </div>
        <div className="ml-3 border-l-2 border-border pl-1">
          <MenuProvider value={contextValue}>{children}</MenuProvider>
        </div>
      </div>
    );
  }

  return (
    <DropdownMenuPrimitive.Sub>
      {/* The same row as every other item, so a submenu trigger highlights in
          brand like its neighbours. It used to hand-roll `hover:bg-foreground`
          — near-black — and key off hover rather than Radix's highlighted state,
          so opening Theme from the keyboard lit nothing and opening it with the
          mouse turned the row dark in the middle of a blue menu. Only the right
          padding differs: the caret needs less room than a plain row's gutter. */}
      <DropdownMenuPrimitive.SubTrigger
        className={menuSubTriggerClasses}
        style={headingFontStyle()}
      >
        {iconSlot}
        <span className="flex-1">{trigger}</span>
        <Icon
          icon={CaretRightIcon}
          size="xs"
          color="text-content-faint group-data-[highlighted]:text-white"
        />
      </DropdownMenuPrimitive.SubTrigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.SubContent
          style={{ zIndex: popoverZ }}
          sideOffset={4}
          collisionPadding={8}
          className="max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-44 overflow-y-auto rounded-md border border-border bg-card py-1 shadow-floating"
        >
          {/* Same reset-then-re-provide as the root menu content. */}
          <PortalContextReset>
            <MenuProvider value={contextValue}>{children}</MenuProvider>
          </PortalContextReset>
        </DropdownMenuPrimitive.SubContent>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Sub>
  );
}
